import * as echarts from 'echarts';

class AnalysisRenderer {
    constructor() {
        this.META = {
            DRIVER: { l: "Antreiber", c: "driver", icon: '<rect x="5" y="5" width="14" height="14" rx="2"></rect>' },
            PLANNER: { l: "Strukturgeber", c: "planner", icon: '<path d="M12 2.5l9.5 9.5-9.5 9.5-9.5-9.5L12 2.5z"></path>' },
            CONNECTOR: { l: "Beziehungsmanager", c: "connector", icon: '<path d="M12 2l8.66 5v10L12 22 3.34 17V7L12 2z"></path>' },
            INNOVATOR: { l: "Zukunftsgestalter", c: "innovator", icon: '<path d="M12 3l10 17H2L12 3z"></path>' }
        };

        this.colors = { 
            rot: '#ef4444', 
            blau: '#3b82f6', 
            grün: '#22c55e', 
            gelb: '#f59e0b',
            slate: '#64748b'
        };

        this.styles = [
            { key: 'DRIVER', name: 'DRIVER', color: this.colors.rot },
            { key: 'PLANNER', name: 'PLANNER', color: this.colors.blau },
            { key: 'CONNECTOR', name: 'CONNECTOR', color: this.colors.grün },
            { key: 'INNOVATOR', name: 'INNOVATOR', color: this.colors.gelb }
        ];

        // Central Configuration for Animations and Charts
        this.CONFIG = {
            MAX_SCORE: 12,
            ANIMATION: {
                DEFAULT_DURATION: 1200,
                PIE_DURATION: 2000,
                EASING: 'cubicOut',
                GAUGE_RESET_DELAY: 50
            },
            GAUGE: {
                MIN: -6,
                MAX: 6,
                WIDTH: 12
            },
            BAR_CHART: {
                MIN: -12,
                MAX: 12,
                GRID: { left: '1%', right: '3%', top: 40, bottom: 20, containLabel: true }
            }
        };
    }

    getStyles() {
        return this.styles;
    }

    clamp(n) { 
        const x = Number(n || 0); 
        return Math.max(0, Math.min(this.CONFIG.MAX_SCORE, x)); 
    }

    alpha(hex, a) {
        const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
        const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
        return `rgba(${r},${g},${b},${a})`;
    }

    getSize(val) {
        return Math.pow(Math.max(0, Number(val || 0)), 1.1); 
    }

    renderBeforeAfter(domChart, styles, dataset) {
        if (!domChart) return;
        const isPrint = window.isPrinting === true;
        const textColor = isPrint ? '#0F1720' : '#ffffff';
        const axisColor = isPrint ? '#0F1720' : '#94a3b8';
        
        const cats = styles.map(s => s.name);
        const absItems = styles.map(s => {
            const abs = this.clamp(dataset?.absicht?.[s.key]);
            return {
                value: -abs,
                itemStyle: { color: s.color, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.10)', borderRadius: 0 },
                label: { show: true, position: 'insideLeft', formatter: () => Math.abs(abs).toString(), color: textColor, fontSize: 11 }
            };
        });
        const stressItems = styles.map(s => {
            const ver = this.clamp(dataset?.verhalten?.[s.key]);
            return {
                value: ver,
                itemStyle: { color: s.color, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.10)', borderRadius: 0 },
                label: { show: true, position: 'insideRight', formatter: ver, color: textColor, fontSize: 11 }
            };
        });
        
        const rich = {};
        styles.forEach(s => {
            rich['dot_' + s.key] = { width: 10, height: 10, borderRadius: 5, backgroundColor: s.color };
            rich['name_' + s.key] = { color: '#0F1720', fontWeight: 600, padding: [2, 8] };
        });

        const option = {
            backgroundColor: isPrint ? '#ffffff' : 'transparent',
            animationDuration: isPrint ? 0 : this.CONFIG.ANIMATION.DEFAULT_DURATION,
            animationEasing: this.CONFIG.ANIMATION.EASING,
            animationDurationUpdate: isPrint ? 0 : this.CONFIG.ANIMATION.DEFAULT_DURATION,
            animationEasingUpdate: this.CONFIG.ANIMATION.EASING,
            grid: this.CONFIG.BAR_CHART.GRID,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: items => {
                    const name = items[0]?.name || '';
                    const left = items.find(i => i.seriesName === 'Absicht');
                    const right = items.find(i => i.seriesName === 'Stress');
                    return `<div class="font-bold">${name}</div>Absicht: ${Math.abs(left?.value ?? 0)}<br/>Verhalten: ${Math.abs(right?.value ?? 0)}`;
                }
            },
            xAxis: {
                type: 'value',
                min: this.CONFIG.BAR_CHART.MIN,
                max: this.CONFIG.BAR_CHART.MAX,
                interval: 2,
                axisLine: { onZero: true, lineStyle: { color: '#cbd5e1' } },
                splitLine: { lineStyle: { color: '#e2e8f0' } },
                axisLabel: { formatter: v => Math.abs(v), color: axisColor }
            },
            yAxis: {
                type: 'category',
                inverse: true,
                data: cats,
                axisLine: { lineStyle: { color: '#cbd5e1' } },
                axisTick: { show: false },
                axisLabel: {
                    margin: 18,
                    color: axisColor,
                    formatter: (val) => {
                        const style = styles.find(x => x.name === val) || styles[0];
                        return `{name_${style.key}|${val}} {dot_${style.key}|}`;
                    },
                    rich
                }
            },
            series: [
                {
                    name: 'Absicht',
                    type: 'bar',
                    data: absItems,
                    barWidth: 22,
                    barGap: '0%',
                    stack: 'balance',
                    itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.12)' }
                },
                {
                    name: 'Stress',
                    type: 'bar',
                    data: stressItems,
                    barWidth: 22,
                    barGap: '0%',
                    stack: 'balance',
                    itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.12)' },
                    markLine: { 
                        silent: true, 
                        symbol: 'none', 
                        label: { show: false }, 
                        lineStyle: { color: '#64748b', type: 'dashed', width: 1.5 },
                        data: [{ xAxis: 0 }] 
                    }
                },
                {
                    type: 'custom',
                    renderItem: function(params, api) {
                        const xPos = api.coord([0, 0])[0];
                        return {
                            type: 'group',
                            x: xPos,
                            y: 5,
                            children: [
                                {
                                    type: 'text',
                                    style: { text: 'Absicht', textAlign: 'right', fill: axisColor, fontSize: 12, fontWeight: 600 },
                                    x: -15, y: 0
                                },
                                {
                                    type: 'text',
                                    style: { text: 'Verhalten', textAlign: 'left', fill: axisColor, fontSize: 12, fontWeight: 600 },
                                    x: 15, y: 0
                                }
                            ]
                        };
                    },
                    data: [0],
                    z: 100,
                    silent: true
                }
            ]
        };

        // Removed clear() to prevent lag/blink during mode switch
        domChart.setOption(option);
    }

    renderPiePadAngleUnified(domChart, styles, values, scenario = 'stress') {
        if (!domChart) return;
        const isPrint = window.isPrinting === true;
        const scen = scenario;
        const absMap = values[scen].absicht;
        const verMap = values[scen].verhalten;
        
        const pairData = [];
        const labelData = [];
        const centerData = [];
        const lines = [];

        // Pre-calculate Total Size for Angles
        let totalSizeAll = 0;
        styles.forEach(s => {
            const abs = this.clamp(absMap?.[s.key]);
            const ver = this.clamp(verMap?.[s.key]);
            totalSizeAll += this.getSize(abs) + this.getSize(ver);
        });

        const w = domChart.getWidth();
        const h = domChart.getHeight();
        const cx = w / 2;
        const cy = h / 2;
        const rMin = Math.min(w, h) / 2;
        
        const rInner = rMin * 0.20;
        const rOuter = rMin * 0.58; 
        const rMax   = rMin * 0.94; 

        // Logo
        const logoSize = rInner * 1.7; 
        const logoGraphic = {
            type: 'image',
            style: {
                image: window.themeUrl ? window.themeUrl + '/assets/img/fingerprint.svg' : 'assets/img/fingerprint.svg',
                width: logoSize,
                height: logoSize,
                x: cx - logoSize / 2,
                y: cy - logoSize / 2
            },
            z: 5
        };
        lines.push(logoGraphic);

        let currentAngle = 180; 

        styles.forEach((s, i) => {
            const abs = this.clamp(absMap?.[s.key]);
            const ver = this.clamp(verMap?.[s.key]);
            const sizeAbs = this.getSize(abs);
            const sizeVer = this.getSize(ver);
            const totalSize = sizeAbs + sizeVer;
            
            // 1. Separator (Start)
            const radStart = currentAngle * Math.PI / 180;
            lines.push({
                type: 'line',
                shape: {
                    x1: cx + (rInner - 2) * Math.cos(radStart),
                    y1: cy + (rInner - 2) * Math.sin(radStart),
                    x2: cx + (rMax + 20) * Math.cos(radStart),
                    y2: cy + (rMax + 20) * Math.sin(radStart)
                },
                style: { stroke: '#ffffff', lineWidth: 8 },
                z: 10,
                silent: true
            });

            // Left (Absicht)
            const absColor = this.alpha(s.color, 0.4);
            pairData.push({
                value: sizeAbs, 
                name: `${s.name} · Absicht`,
                key: s.key,
                realValue: abs,
                itemStyle: { color: absColor, borderWidth: 0 },
                label: { show: true, position: 'inside', formatter: (p) => p.data.realValue, color: isPrint ? '#0F1720' : '#ffffff', fontSize: 11, fontWeight: 700 }
            });
            
            labelData.push({
                value: sizeAbs, 
                name: `${s.name} · Absicht`,
                itemStyle: { color: 'transparent', borderWidth: 0 },
                label: { show: true, position: 'outside', formatter: 'Absicht', color: s.color, fontSize: 11, fontWeight: 400, lineHeight: 14, align: 'center' },
                labelLine: { show: true, length: 15, length2: 10, lineStyle: { color: s.color, width: 2 } },
                labelLayout: { hideOverlap: true }
            });

            const angleAbs = (sizeAbs / totalSizeAll) * 360;
            
            // 2. Separator (Middle)
            const angleSplit = currentAngle + angleAbs;
            const radSplit = angleSplit * Math.PI / 180;
            lines.push({
                type: 'line',
                shape: {
                    x1: cx + (rOuter - 1) * Math.cos(radSplit),
                    y1: cy + (rOuter - 1) * Math.sin(radSplit),
                    x2: cx + (rMax + 20) * Math.cos(radSplit),
                    y2: cy + (rMax + 20) * Math.sin(radSplit)
                },
                style: { stroke: '#ffffff', lineWidth: 3 },
                z: 10,
                silent: true
            });

            // Right (Verhalten)
            pairData.push({
                value: sizeVer,
                name: `${s.name} · Verhalten`,
                key: s.key,
                realValue: ver,
                itemStyle: { color: s.color, borderWidth: 0 },
                label: { show: true, position: 'inside', formatter: (p) => p.data.realValue, color: '#ffffff', fontSize: 11, fontWeight: 700 }
            });

            labelData.push({
                value: sizeVer, 
                name: `${s.name} · Verhalten`,
                itemStyle: { color: 'transparent', borderWidth: 0 },
                label: { show: true, position: 'outside', formatter: 'Verhalten', color: s.color, fontSize: 11, fontWeight: 400, lineHeight: 14, align: 'center' },
                labelLine: { show: true, length: 15, length2: 10, lineStyle: { color: s.color, width: 2 } },
                labelLayout: { hideOverlap: true }
            });
            
            centerData.push({
                value: totalSize,
                name: s.name,
                key: s.key,
                itemStyle: { color: s.color, borderWidth: 0 },
                label: { show: true, position: 'inside', color: '#ffffff', fontSize: 9, fontWeight: 600, formatter: s.name.toUpperCase(), rotate: 0 }
            });

            const angleVer = (sizeVer / totalSizeAll) * 360;
            currentAngle += (angleAbs + angleVer);
        });

        const option = {
            backgroundColor: isPrint ? '#ffffff' : 'transparent',
            animationDuration: isPrint ? 0 : this.CONFIG.ANIMATION.PIE_DURATION,
            animationEasing: this.CONFIG.ANIMATION.EASING,
            animationDurationUpdate: isPrint ? 0 : this.CONFIG.ANIMATION.PIE_DURATION,
            animationEasingUpdate: this.CONFIG.ANIMATION.EASING,
            stateAnimation: {
                duration: this.CONFIG.ANIMATION.PIE_DURATION,
                easing: this.CONFIG.ANIMATION.EASING
            },
            tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(15,23,42,0.92)',
                borderWidth: 0,
                textStyle: { color: '#ffffff' },
                formatter: (p) => {
                    const key = p.data?.key;
                    if (!key) return '';
                    const abs = this.clamp(absMap?.[key]);
                    const ver = this.clamp(verMap?.[key]);
                    return `${p.name}<br/>Absicht: ${abs} · Verhalten: ${ver}`;
                }
            },
            graphic: { elements: lines },
            series: [
                { name: 'Style Name', type: 'pie', startAngle: 180, radius: ['20%', '55%'], center: ['50%', '50%'], avoidLabelOverlap: true, itemStyle: { borderWidth: 0 }, data: centerData, silent: true },
                { name: 'Absicht/Verhalten', type: 'pie', startAngle: 180, clockwise: true, radius: ['58%', '94%'], center: ['50%', '50%'], avoidLabelOverlap: true, itemStyle: { borderWidth: 0 }, data: pairData },
                { name: 'Absicht/Verhalten Labels', type: 'pie', startAngle: 180, clockwise: true, radius: ['58%', '94%'], center: ['50%', '50%'], avoidLabelOverlap: true, itemStyle: { borderWidth: 0 }, silent: true, data: labelData }
            ]
        };
        
        domChart.setOption(option);
    }

    renderGauge(domChart, name, color, diff) {
        if (!domChart) return;
        const isPrint = window.isPrinting === true;
        const min = this.CONFIG.GAUGE.MIN;
        const max = this.CONFIG.GAUGE.MAX;
        const val = Math.max(min, Math.min(max, diff));
        const center = ['50%', '60%'];
        const radius = '100%';
        const width = this.CONFIG.GAUGE.WIDTH;
        
        const option = {
            backgroundColor: isPrint ? '#ffffff' : 'transparent',
            animationDuration: isPrint ? 0 : this.CONFIG.ANIMATION.DEFAULT_DURATION,
            animationEasing: this.CONFIG.ANIMATION.EASING,
            animationDurationUpdate: isPrint ? 0 : this.CONFIG.ANIMATION.DEFAULT_DURATION,
            animationEasingUpdate: this.CONFIG.ANIMATION.EASING,
            series: [
                {
                    type: 'gauge', min: min, max: max, radius: radius, center: center, startAngle: 180, endAngle: 0,
                    axisLine: { roundCap: true, lineStyle: { width: width, color: [[1, '#e2e8f0']] } },
                    splitLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false }, pointer: { show: false }, detail: { show: false },
                    data: [{ value: min }]
                },
                {
                    type: 'custom', name: 'Fill', coordinateSystem: 'none', data: [val],
                    renderItem: (params, api) => {
                        if (api.value(0) === undefined || isNaN(api.value(0))) return;
                        const valRaw = api.value(0);
                        const val = Math.max(this.CONFIG.GAUGE.MIN, Math.min(this.CONFIG.GAUGE.MAX, valRaw)); // Hard clamp to prevent full circle glitch
                        const w = api.getWidth();
                        const h = api.getHeight();
                        const cx = w * 0.5;
                        const cy = h * 0.6;
                        if (!w || !h) return;
                        const r = Math.min(w, h) / 2;
                        const width = this.CONFIG.GAUGE.WIDTH; 
                        const r0 = r - width;
                        const startAngle = 1.5 * Math.PI; 
                        let angleOffset = (Math.abs(val) / this.CONFIG.GAUGE.MAX) * (Math.PI / 2);
                        if (Math.abs(val) > 0 && Math.abs(val) <= 1) angleOffset = Math.max(angleOffset, 0.08);
                        const endAngle = val >= 0 ? startAngle + angleOffset : startAngle - angleOffset;
                        return {
                            type: 'sector',
                            shape: { cx: cx, cy: cy, r: r, r0: r0, startAngle: startAngle, endAngle: endAngle, clockwise: val >= 0, cornerRadius: 6 },
                            style: { fill: color },
                            transition: ['shape']
                        };
                    },
                    z: 2
                },
                {
                    type: 'gauge', min: min, max: max, radius: radius, center: center, startAngle: 180, endAngle: 0,
                    axisLine: { lineStyle: { width: 0, color: [[1, 'transparent']] } },
                    pointer: { show: true, length: '60%', width: 4, itemStyle: { color } },
                    anchor: { show: true, showAbove: true, size: 8, itemStyle: { color: '#ffffff', borderColor: '#cbd5e1', borderWidth: 2 } },
                    splitLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false },
                    title: { show: true, offsetCenter: [0, '30%'], color: '#0f172a', fontSize: 11, fontWeight: 600 },
                    detail: { 
                        valueAnimation: !isPrint, 
                        formatter: (v) => { 
                            const sign = v>0?'Push':v<0?'Pull':'Stabil'; 
                            const sgn = v>0?'+':v<0?'':''
                            return `${sign} ${sgn}${Math.round(v)}`; 
                        }, 
                        color, fontSize: 12, offsetCenter: [0, '55%'] 
                    },
                    data: [{ value: val, name }]
                }
            ]
        };
        if (!isPrint) {
            // Force reset to 0 state first (no animation)
            domChart.setOption({
                series: [
                    {},
                    { data: [0] },
                    { data: [{ value: 0, name }] }
                ],
                animation: false
            }, { notMerge: false, lazyUpdate: false });
            
            // Then animate to new value
            setTimeout(() => {
                option.animation = true;
                option.animationDuration = this.CONFIG.ANIMATION.DEFAULT_DURATION;
                domChart.setOption(option);
            }, this.CONFIG.ANIMATION.GAUGE_RESET_DELAY);
        } else {
            domChart.setOption(option);
        }
        domChart.resize();
    }

    renderOverviewTable(container, styles, values) {
        if (!container) return;
        
        let html = `
        <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                 <h3 class="font-semibold text-slate-900">Daten-Übersicht</h3>
                 <p class="text-xs text-slate-500 mt-0.5">Detaillierte Werte für Normal- und Stress-Modus</p>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full text-xs text-left whitespace-nowrap">
                    <thead>
                        <tr class="bg-slate-50 border-b border-slate-200 text-slate-500">
                            <th class="px-4 py-3 font-semibold uppercase tracking-wider">Stil</th>
                            
                            <th colspan="4" class="px-3 py-2 font-semibold text-center uppercase tracking-wider border-l border-slate-200 bg-slate-100/50 text-slate-600">Normal</th>
                            <th colspan="4" class="px-3 py-2 font-semibold text-center uppercase tracking-wider border-l border-slate-200 bg-slate-100/50 text-slate-600">Stress</th>
                        </tr>
                        <tr class="bg-white border-b border-slate-100 text-slate-500">
                            <th class="px-4 py-2"></th>
                            
                            <th class="px-3 py-2 font-medium border-l border-slate-100">Absicht</th>
                            <th class="px-3 py-2 font-medium">Verhalten</th>
                            <th class="px-3 py-2 font-medium">Gesamt</th>
                            <th class="px-3 py-2 font-medium">Push / Pull</th>
                            
                            <th class="px-3 py-2 font-medium border-l border-slate-100">Absicht</th>
                            <th class="px-3 py-2 font-medium">Verhalten</th>
                            <th class="px-3 py-2 font-medium">Gesamt</th>
                            <th class="px-3 py-2 font-medium">Push / Pull</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100">
        `;
        
        styles.forEach(s => {
            const k = s.key;
            const nAbs = values.normal.absicht[k];
            const nVer = values.normal.verhalten[k];
            const nTot = nAbs + nVer;
            const nDiff = nVer - nAbs;
            const nPP = nDiff > 0 ? `<span class="text-indigo-600 font-bold">+${nDiff}</span>` : (nDiff < 0 ? `<span class="text-rose-600 font-bold">${nDiff}</span>` : `<span class="text-emerald-600 font-bold">0</span>`);
            
            const sAbs = values.stress.absicht[k];
            const sVer = values.stress.verhalten[k];
            const sTot = sAbs + sVer;
            const sDiff = sVer - sAbs;
            const sPP = sDiff > 0 ? `<span class="text-indigo-600 font-bold">+${sDiff}</span>` : (sDiff < 0 ? `<span class="text-rose-600 font-bold">${sDiff}</span>` : `<span class="text-emerald-600 font-bold">0</span>`);
            
            html += `
                <tr class="hover:bg-slate-50/50 transition-colors">
                    <td class="px-4 py-3 font-bold" style="color:${s.color}">${s.name}</td>
                    
                    <td class="px-3 py-3 border-l border-slate-100">${nAbs}</td>
                    <td class="px-3 py-3">${nVer}</td>
                    <td class="px-3 py-3 font-bold text-slate-700">${nTot}</td>
                    <td class="px-3 py-3">${nPP}</td>
                    
                    <td class="px-3 py-3 border-l border-slate-100">${sAbs}</td>
                    <td class="px-3 py-3">${sVer}</td>
                    <td class="px-3 py-3 font-bold text-slate-700">${sTot}</td>
                    <td class="px-3 py-3">${sPP}</td>
                </tr>
            `;
        });
        
        html += `
                    </tbody>
                </table>
            </div>
        </div>
        `;
        
        container.innerHTML = html;
    }

    buildGlobalSummary(json, styles, values, scenario = 'stress') {
        const dom = { globalSummary: document.getElementById('globalSummary') || document.getElementById('globalSummarySection') };
        if (!dom.globalSummary) return;
        dom.globalSummary.innerHTML = '';
        dom.globalSummary.className = 'hidden';

        const summary = json?.global_summary || json;
        // Require at least summary or values to render something
        if ((!summary || (!summary.dominant_style && !summary.stress_profile)) && !values) return;

        dom.globalSummary.className = 'bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8 block';
        // Add subtle ambient background
        dom.globalSummary.style.background = `radial-gradient(circle at 50% 0%, #f8fafc 0%, #ffffff 100%)`;
        
        let html = `
            <div class="flex items-center gap-3 mb-6">
                <h3 class="text-lg font-bold text-slate-900">Zusammenfassung & Profil-Analyse</h3>
            </div>
            <div class="space-y-6 text-slate-700 leading-relaxed text-justify mb-8">
        `;
        
        // Single Block of Text
        let combinedText = '';
        if (summary?.dominant_style) {
            combinedText += summary.dominant_style;
        }
        if (summary?.stress_profile) {
            // Add a spacer if we have both parts
            if (combinedText) combinedText += ' ';
            combinedText += summary.stress_profile;
        }
        
        if (combinedText) {
             // Colorize Style Names in Summary
             styles.forEach(s => {
                // Match "driver", "planner", etc. case-insensitive
                // Use the key (driver, planner...) to find matches in the text
                const regex = new RegExp(s.key, 'gi');
                combinedText = combinedText.replace(regex, (match) => {
                     // Keep the original casing (match) but wrap in colored span
                     return `<span style="color: ${s.color}; font-weight: 700;">${match}</span>`;
                });
             });
             html += `<div class="prose-slate"><p>${combinedText}</p></div>`;
        }
        
        html += '</div>';

        // New Section: Detailed Analysis (4 Styles / 2 Behaviors)
        if (styles && values) {
            html += `
                <div class="border-t border-slate-100 pt-6">
                    <h4 class="font-bold text-slate-900 mb-6 text-sm uppercase tracking-wide">Detaillierte Stil-Dynamik (4 Stile / 2 Ebenen)</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            `;

            styles.forEach(s => {
                const k = s.key;
                const nAbs = values.normal.absicht[k];
                const nVer = values.normal.verhalten[k];
                const sAbs = values.stress.absicht[k];
                const sVer = values.stress.verhalten[k];
                
                // --- Normal Text ---
                let textNormal = "";
                const nDiff = nVer - nAbs;
                if (nDiff === 0) textNormal += `Im Alltag sind Absicht und Verhalten mit jeweils <strong>${nAbs}</strong> Punkten deckungsgleich. `;
                else if (nDiff > 0) textNormal += `Im Alltag zeigt sich ein starkes Verhalten (<strong>${nVer}</strong>) bei moderater Absicht (<strong>${nAbs}</strong>). `;
                else textNormal += `Im Alltag dominiert die Absicht (<strong>${nAbs}</strong>) gegenüber dem Verhalten (<strong>${nVer}</strong>). `;
                
                // Normal PP
                if (nDiff > 0) textNormal += `Es entsteht eine <span class="text-indigo-600 font-bold">Push-Dynamik (+${nDiff})</span>.`;
                else if (nDiff < 0) textNormal += `Es entsteht eine <span class="text-rose-600 font-bold">Pull-Dynamik (${nDiff})</span>.`;
                else textNormal += `Absicht und Verhalten sind <span class="text-emerald-600 font-bold">ausgeglichen</span>.`;

                // --- Stress Text ---
                let textStress = "";
                // Stress Logic
                if (sAbs > nAbs && sVer > nVer) textStress += `Unter Druck steigen beide Werte an (A: <strong>${sAbs}</strong>, V: <strong>${sVer}</strong>). `;
                else if (sAbs < nAbs && sVer < nVer) textStress += `Unter Druck sinken beide Werte (A: <strong>${sAbs}</strong>, V: <strong>${sVer}</strong>). `;
                else textStress += `Unter Druck verändert sich das Profil zu Absicht <strong>${sAbs}</strong> und Verhalten <strong>${sVer}</strong>. `;
                
                const sDiff = sVer - sAbs;
                 // Stress PP
                if (sDiff > 0) textStress += `Es entsteht eine <span class="text-indigo-600 font-bold">Push-Dynamik (+${sDiff})</span>.`;
                else if (sDiff < 0) textStress += `Es entsteht eine <span class="text-rose-600 font-bold">Pull-Dynamik (${sDiff})</span>.`;
                else textStress += `Absicht und Verhalten sind <span class="text-emerald-600 font-bold">ausgeglichen</span>.`;

                html += `
                    <div class="bg-slate-50/50 rounded-lg p-5 border border-slate-100 relative overflow-hidden group hover:bg-white hover:shadow-md transition-all">
                        <div class="absolute top-0 left-0 w-1 h-full" style="background:${s.color}"></div>
                        <div class="flex items-center gap-2 mb-3">
                             <span class="font-bold text-sm uppercase tracking-wider" style="color:${s.color}">${s.name}</span>
                        </div>
                        <div class="space-y-3">
                            <div>
                                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-1">Normal (Alltag)</span>
                                <p class="text-xs leading-relaxed text-slate-600 text-justify">
                                    ${textNormal}
                                </p>
                            </div>
                            <div>
                                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-1">Stress (Druck)</span>
                                <p class="text-xs leading-relaxed text-slate-600 text-justify">
                                    ${textStress}
                                </p>
                            </div>
                        </div>
                    </div>
                `;
            });

            html += `
                    </div>
                </div>
            `;
        }

        dom.globalSummary.innerHTML = html;
    }

    buildEvaluationRow(json, styles, values, scenario, dummyData) {
        const dom = { 
            evalRow: document.getElementById('evalRow') || document.getElementById('evaluationRow'),
            evalCleanups: [] 
        };
        if (!dom.evalRow) return;
        
        const isPrint = window.isPrinting === true;
        
        // Cleanup previous listeners and charts
        if (window.evalCleanups) {
            window.evalCleanups.forEach(f => f());
        }
        window.evalCleanups = [];

        dom.evalRow.innerHTML = '';
        
        // Create Style Cards
        styles.forEach(s => {
            // Values
            const valAbs = values[scenario].absicht[s.key];
            const valVer = values[scenario].verhalten[s.key];
            
            const fallbackData = dummyData.getEvaluationData(s.key);
            
            const rawData = values.comp?.[s.name] || values.comp?.[s.key];
            const hasContent = rawData?.report?.dimensions && Object.keys(rawData.report.dimensions).length > 0;
            const styleData = hasContent ? rawData : fallbackData;

            // --- Logic for Print vs Screen ---
            let contentHTML = '';
            
            if (isPrint) {
                // PRINT MODE: Show Both Normal and Stress Texts
                
                // 1. Normal Texts
                let nAbs = '-', nVer = '-';
                if (styleData?.report?.dimensions) {
                    nAbs = styleData.report.dimensions.Absicht_normal || '-';
                    nVer = styleData.report.dimensions.Verhalten_normal || '-';
                }
                
                // 2. Stress Texts
                let sAbs = '-', sVer = '-';
                if (styleData?.report?.dimensions) {
                    sAbs = styleData.report.dimensions.Absicht_stress || '-';
                    sVer = styleData.report.dimensions.Verhalten_stress || '-';
                }
                
                contentHTML = `
                    <div class="grid grid-cols-2 gap-4">
                        <!-- Normal Section -->
                        <div class="relative pl-3 border-l-2 border-slate-200">
                            <span class="text-[10px] font-bold uppercase text-slate-500 mb-1 block tracking-wider">Normal (Alltag)</span>
                            <div class="space-y-2">
                                <div>
                                    <span class="text-[9px] uppercase font-bold block" style="color:${this.alpha(s.color, 0.8)}">Absicht</span>
                                    <p class="text-xs text-slate-800 leading-snug">${nAbs}</p>
                                </div>
                                <div>
                                    <span class="text-[9px] uppercase font-bold block" style="color:${s.color}">Verhalten</span>
                                    <p class="text-xs text-slate-800 leading-snug">${nVer}</p>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Stress Section -->
                        <div class="relative pl-3 border-l-2 border-slate-200">
                            <span class="text-[10px] font-bold uppercase text-slate-500 mb-1 block tracking-wider">Stress (Druck)</span>
                            <div class="space-y-2">
                                <div>
                                    <span class="text-[9px] uppercase font-bold block" style="color:${this.alpha(s.color, 0.8)}">Absicht</span>
                                    <p class="text-xs text-slate-800 leading-snug">${sAbs}</p>
                                </div>
                                <div>
                                    <span class="text-[9px] uppercase font-bold block" style="color:${s.color}">Verhalten</span>
                                    <p class="text-xs text-slate-800 leading-snug">${sVer}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
                
            } else {
                // SCREEN MODE: Standard Single Scenario
                let textAbs = 'Beschreibung der Absicht...';
                let textVer = 'Beschreibung des Verhaltens...';
                
                if (styleData && styleData.report && styleData.report.dimensions) {
                    if (scenario === 'normal') {
                        textAbs = styleData.report.dimensions.Absicht_normal || '-';
                        textVer = styleData.report.dimensions.Verhalten_normal || '-';
                    } else {
                        textAbs = styleData.report.dimensions.Absicht_stress || '-';
                        textVer = styleData.report.dimensions.Verhalten_stress || '-';
                    }
                }
                
                contentHTML = `
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <span class="text-[10px] uppercase font-bold block mb-2 flex items-center gap-2" style="color:${this.alpha(s.color, 0.6)}">
                                <span class="w-1.5 h-1.5 rounded-full" style="background:${this.alpha(s.color, 0.6)}"></span>
                                Absicht
                            </span>
                            <p class="text-sm text-slate-600 leading-relaxed text-justify">${textAbs}</p>
                        </div>
                        <div>
                            <span class="text-[10px] uppercase font-bold block mb-2 flex items-center gap-2" style="color:${s.color}">
                                <span class="w-1.5 h-1.5 rounded-full" style="background:${s.color}"></span>
                                Verhalten
                            </span>
                            <p class="text-sm text-slate-600 leading-relaxed text-justify">${textVer}</p>
                        </div>
                    </div>
                `;
            }

            // Push Pull Calculation
            const ppDiff = valVer - valAbs;
            const ppType = ppDiff > 0 ? 'Push' : (ppDiff < 0 ? 'Pull' : 'Balance');
            const ppColor = ppDiff > 0 ? 'text-indigo-600' : (ppDiff < 0 ? 'text-rose-600' : 'text-emerald-600');
            const ppBg = ppDiff > 0 ? 'bg-indigo-50' : (ppDiff < 0 ? 'bg-rose-50' : 'bg-emerald-50');
            const ppBorder = ppDiff > 0 ? 'border-indigo-100' : (ppDiff < 0 ? 'border-rose-100' : 'border-emerald-100');
            const ppDot = ppDiff > 0 ? 'bg-indigo-500' : (ppDiff < 0 ? 'bg-rose-500' : 'bg-emerald-500');

            let ppText = '';
            if (styleData && styleData.push_pull_context) {
                if (scenario === 'normal') {
                    ppText = styleData.push_pull_context.normal?.text || '';
                } else {
                    ppText = styleData.push_pull_context.stress?.text || '';
                }
            }
            
            if (!ppText) {
                ppText = ppDiff === 0 
                    ? 'Absicht und Verhalten sind in diesem Szenario ausgeglichen.' 
                    : (ppDiff > 0 
                        ? 'Die innere Absicht ist stärker ausgeprägt als das beobachtbare Verhalten.' 
                        : 'Das Verhalten ist stärker ausgeprägt als die innere Absicht.');
            }

            const card = document.createElement('div');
            card.className = 'break-inside-avoid bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col relative';
            card.style.borderLeft = `6px solid ${s.color}`;
            card.style.background = `radial-gradient(circle at 95% 50%, ${this.alpha(s.color, 0.08)} 0%, transparent 40%)`;
            card.style.backgroundColor = '#ffffff';
            
            const chartId = `chart_eval_${s.key}`;
            
            card.innerHTML = `
                <div class="grid grid-cols-1 lg:grid-cols-12 gap-0">
                    <!-- Text Content -->
                    <div class="col-span-12 lg:col-span-8 p-6 flex flex-col gap-6">
                        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
                             <h4 class="font-bold text-slate-900 uppercase tracking-wide text-lg">${s.name}</h4>
                             <div class="flex gap-6">
                                <div class="text-right">
                                    <span class="block text-[10px] uppercase font-bold tracking-wider" style="color:${this.alpha(s.color, 0.6)}">Absicht</span>
                                    <span class="text-xl font-bold" style="color:${this.alpha(s.color, 0.6)}">${valAbs}</span>
                                </div>
                                <div class="text-right">
                                    <span class="block text-[10px] uppercase font-normal tracking-wider" style="color:${s.color}">Verhalten</span>
                                    <span class="text-xl font-normal" style="color:${s.color}">${valVer}</span>
                                </div>
                             </div>
                        </div>
                        
                        ${contentHTML}

                        <div class="${ppBg} p-4 rounded-lg border ${ppBorder} mt-auto">
                            <div class="flex items-center justify-between mb-2">
                                <span class="text-[10px] uppercase font-bold ${ppColor} flex items-center gap-1.5">
                                    <span class="w-2 h-2 rounded-full ${ppDot}"></span>
                                    Push- / Pull-Dynamik
                                </span>
                                <span class="text-xs font-bold ${ppColor} px-2 py-0.5 bg-white/50 rounded border ${ppBorder}">
                                    ${ppType} ${ppDiff > 0 ? '+' : ''}${ppDiff}
                                </span>
                            </div>
                            <p class="text-sm text-slate-600 italic leading-relaxed">
                                ${ppText}
                            </p>
                        </div>
                    </div>

                    <!-- Chart Section -->
                    <div class="col-span-12 lg:col-span-4 border-l border-slate-50 p-6 flex flex-col justify-center relative">
                        <div id="${chartId}" class="w-full h-[220px]"></div>
                    </div>
                </div>
            `;
            dom.evalRow.appendChild(card);

            // Init Chart
            setTimeout(() => {
                const chartDom = document.getElementById(chartId);
                if(chartDom) {
                    const maxVal = 12;
                    const myChart = echarts.init(chartDom, null, { renderer: 'canvas' });
                    
                    const option = {
                        angleAxis: {
                            max: maxVal,
                            startAngle: 90,
                            splitLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: { show: false }
                        },
                        radiusAxis: {
                            type: 'category',
                            data: ['Absicht', 'Verhalten'],
                            z: 10,
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: { show: false }
                        },
                        polar: {
                            radius: ['55%', '90%']
                        },
                        series: [
                            {
                                type: 'bar',
                                data: [maxVal, maxVal],
                                coordinateSystem: 'polar',
                                barWidth: 14,
                                itemStyle: { color: '#f8fafc' }, 
                                silent: true,
                                barGap: '-100%',
                                z: 0,
                                roundCap: true
                            },
                            {
                                type: 'bar',
                                data: [
                                    { 
                                        value: valAbs, 
                                        itemStyle: { 
                                            color: this.alpha(s.color, 0.35),
                                            shadowBlur: 0
                                        } 
                                    }, 
                                    { 
                                        value: valVer, 
                                        itemStyle: { 
                                            color: s.color,
                                            shadowBlur: 8,
                                            shadowColor: this.alpha(s.color, 0.4)
                                        } 
                                    }
                                ],
                                coordinateSystem: 'polar',
                                name: 'Werte',
                                barWidth: 14,
                                z: 1,
                                roundCap: true,
                                animationDuration: isPrint ? 0 : 1500,
                                animationEasing: 'cubicOut'
                            }
                        ],
                        graphic: [
                            {
                                type: 'text',
                                left: 'center',
                                top: '42%',
                                style: {
                                    text: valVer,
                                    textAlign: 'center',
                                    fill: s.color,
                                    fontSize: 32,
                                    fontWeight: '700',
                                    fontFamily: 'sans-serif'
                                }
                            },
                            {
                                type: 'text',
                                left: 'center',
                                top: '62%',
                                style: {
                                    text: valAbs,
                                    textAlign: 'center',
                                    fill: this.alpha(s.color, 0.6),
                                    fontSize: 16,
                                    fontWeight: '700',
                                    fontFamily: 'sans-serif'
                                }
                            }
                        ],
                        tooltip: {
                            show: true,
                            formatter: '{b}: {c}'
                        }
                    };
                    myChart.setOption(option);
                    
                    // Add resize listener and store cleanup
                    const resizeHandler = () => myChart.resize();
                    window.addEventListener('resize', resizeHandler);
                    
                    window.evalCleanups.push(() => {
                        window.removeEventListener('resize', resizeHandler);
                        myChart.dispose();
                    });
                }
            }, 0);
        });
    }

    renderResultsApp(mode, sourceData, dummyData) {
        const isPrint = window.isPrinting === true;
        const app = document.getElementById('resultsApp');
        if (!app) return;
        
        const baseData = dummyData.getData();
        const d = sourceData?.[mode] || baseData[mode];
        
        app.innerHTML = Object.keys(d).map(k => {
            const { a, v } = d[k], diff = a - v, m = this.META[k];
            let pp = 'Stimmig';
            let icon = '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';

            if (diff > 0) {
                pp = 'Pull';
                icon = '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7-7-7M12 5v14"></path></svg>';
            } else if (diff < 0) {
                pp = 'Push';
                icon = '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7 7 7M12 19V5"></path></svg>';
            }

            const hint = diff === 0 ? 'Hohe Stimmigkeit' : Math.abs(diff) <= 2 ? 'Leichte Abweichung' : Math.abs(diff) <= 5 ? 'Warnsignal' : 'Klare Diskrepanz';
            
            let textContent = '';
            if (isPrint) {
                const textNormal = sourceData?.normal?.[k]?.text || baseData?.normal?.[k]?.text || '';
                const textStress = sourceData?.stress?.[k]?.text || baseData?.stress?.[k]?.text || '';
                textContent = `
                    <div class="space-y-6">
                        <div>
                            <span class="text-xs font-bold uppercase text-gray-400 mb-2 block tracking-wider">Normal (Alltag)</span>
                            <div class="text-sm text-gray-700 leading-relaxed">${textNormal}</div>
                        </div>
                        <div class="pt-4 border-t border-gray-100">
                            <span class="text-xs font-bold uppercase text-gray-400 mb-2 block tracking-wider">Stress (Druck)</span>
                            <div class="text-sm text-gray-700 leading-relaxed">${textStress}</div>
                        </div>
                    </div>
                `;
            } else {
                textContent = d[k]?.text || baseData?.[mode]?.[k]?.text || '';
            }

            return `
            <article class="break-inside-avoid rounded-3xl border border-gray-200 bg-white shadow-sm hover:shadow-xl transition-all overflow-hidden">
                <header class="p-6 bg-gradient-to-r from-${m.c} to-${m.c}/80 text-white">
                    <div class="flex items-start justify-between">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-md text-${m.c}">
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">${m.icon}</svg>
                            </div>
                            <div><h5 class="font-display text-2xl font-bold">${k}</h5><p class="text-white/80 text-sm">${m.l}</p></div>
                        </div>
                        <div class="text-right">
                            <span class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-white text-${m.c} shadow-sm">${icon}${Math.abs(diff)} ${pp}</span>
                            <p class="text-white/70 text-xs mt-1">${hint}</p>
                        </div>
                    </div>
                </header>
                <div class="p-6 space-y-4">
                    <div class="grid grid-cols-2 gap-4">
                        <div class="rounded-xl p-4 bg-${m.c}-light/40 border border-gray-200">
                            <p class="text-sm font-semibold text-gray-700 mb-2">Absicht</p>
                            <div class="h-3 rounded-full bg-gray-100 overflow-hidden"><div class="h-full rounded-full bg-${m.c}" style="width:${a/12*100}%"></div></div>
                            <p class="text-right text-sm font-bold mt-1">${a}/12</p>
                        </div>
                        <div class="rounded-xl p-4 bg-gray-50 border border-gray-200">
                            <p class="text-sm font-semibold text-gray-700 mb-2">Verhalten</p>
                            <div class="h-3 rounded-full bg-gray-100 overflow-hidden"><div class="h-full rounded-full bg-${m.c}" style="width:${v/12*100}%"></div></div>
                            <p class="text-right text-sm font-bold mt-1">${v}/12</p>
                        </div>
                    </div>
                    <div class="rounded-xl p-4 bg-white border border-gray-200 flex justify-between items-center">
                        <div><p class="text-sm font-semibold text-gray-700">Einordnung</p><p class="text-sm text-gray-600">${pp}: ${diff > 0 ? 'Absicht stärker' : diff < 0 ? 'Verhalten stärker' : 'Ausgeglichen'}</p></div>
                        <div class="text-right"><p class="text-xs text-gray-400">Differenz</p><p class="text-lg font-bold">${diff > 0 ? '+' : diff < 0 ? '−' : ''}${Math.abs(diff)}</p></div>
                    </div>
                    <div id="analysis-text-${k}" class="rounded-xl p-4 bg-gray-50 border border-gray-200 text-sm text-gray-700 leading-relaxed">
                        ${textContent}
                    </div>
                </div>
            </article>`;
        }).join('');
    }
}

export default AnalysisRenderer;
